import React, { useState } from 'react';
import { authService } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
    KeyRound,
    ShieldCheck,
    ShieldAlert,
    Loader2,
    Lock
} from 'lucide-react';

export default function SettingsPage() {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        current_password: '',
        new_password: '',
        confirm_password: '',
    });

    const validatePassword = (password) => {
        const minLength = password.length >= 8;
        const hasUpper = /[A-Z]/.test(password);
        const hasLower = /[a-z]/.test(password);
        const hasNumber = /[0-9]/.test(password);

        return {
            minLength,
            hasUpper,
            hasLower,
            hasNumber
        };
    };

    const checks = validatePassword(formData.new_password);

    const handleChangePassword = async (e) => {
        e.preventDefault();

        if (formData.new_password !== formData.confirm_password) {
            toast.error('New passwords do not match');
            return;
        }

        if (!Object.values(checks).every(Boolean)) {
            toast.error('Password does not meet requirements');
            return;
        }

        setLoading(true);
        try {
            await authService.changePassword({
                current_password: formData.current_password,
                new_password: formData.new_password
            });
            toast.success('Password changed successfully. Please log in again.');
            // Optional: logout user
            setTimeout(() => {
                localStorage.removeItem('hrms_token');
                localStorage.removeItem('hrms_user');
                window.location.href = '/login';
            }, 2000);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to change password');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto space-y-6 fade-in">
            <div>
                <h1 className="text-xl sm:text-3xl font-bold tracking-tight">Settings</h1>
                <p className="text-muted-foreground mt-1">
                    Manage your account security and preferences
                </p>
            </div>

            <Card className="border-primary/10 bg-card/50 backdrop-blur-sm">
                <CardHeader>
                    <div className="flex items-center gap-2">
                        <KeyRound className="w-5 h-5 text-primary" />
                        <CardTitle>Change Password</CardTitle>
                    </div>
                    <CardDescription>
                        Update your password to keep your account secure
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleChangePassword} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="current_password">Current Password</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    id="current_password"
                                    type="password"
                                    autoComplete="current-password"
                                    className="pl-10"
                                    value={formData.current_password}
                                    onChange={(e) => setFormData({ ...formData, current_password: e.target.value })}
                                    required
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="new_password">New Password</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    id="new_password"
                                    type="password"
                                    autoComplete="new-password"
                                    className="pl-10"
                                    value={formData.new_password}
                                    onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                                    required
                                />
                            </div>

                            {/* password requirements */}
                            <div className="grid grid-cols-2 gap-2 mt-2">
                                <RequirementItem label="8+ Characters" satisfied={checks.minLength} />
                                <RequirementItem label="Uppercase" satisfied={checks.hasUpper} />
                                <RequirementItem label="Lowercase" satisfied={checks.hasLower} />
                                <RequirementItem label="Number" satisfied={checks.hasNumber} />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="confirm_password">Confirm New Password</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    id="confirm_password"
                                    type="password"
                                    autoComplete="new-password"
                                    className="pl-10"
                                    value={formData.confirm_password}
                                    onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                                    required
                                />
                            </div>
                        </div>

                        <Button
                            type="submit"
                            className="w-full mt-4"
                            disabled={loading || !Object.values(checks).every(Boolean)}
                        >
                            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Update Password
                        </Button>
                    </form>
                </CardContent>
            </Card>

            <Card className="border-yellow-500/20 bg-yellow-500/5">
                <CardHeader>
                    <div className="flex items-center gap-2">
                        <ShieldAlert className="w-5 h-5 text-yellow-500" />
                        <CardTitle className="text-yellow-500">Security Note</CardTitle>
                    </div>
                </CardHeader>
                <CardContent>
                    <p className="text-sm text-yellow-500/80">
                        Changing your password will invalidate all your current active sessions.
                        You will need to log in again on all your devices.
                    </p>
                </CardContent>
            </Card>
        </div>
    );
}

const RequirementItem = ({ label, satisfied }) => (
    <div className={`flex items-center gap-2 text-xs ${satisfied ? 'text-green-500' : 'text-muted-foreground'}`}>
        {satisfied ? <ShieldCheck className="w-3 h-3" /> : <div className="w-3 h-3 rounded-full border border-current" />}
        {label}
    </div>
);
